import type { MotionScene } from '@/lib/motion/types';

/** Fractional (0–1) rectangle relative to the composition width/height. */
export interface LayerRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LayerShadow {
  color: string;
  blur: number;
  x: number;
  y: number;
}

/** Full CapCut-style text styling. fontSize/strokeWidth/shadow offsets are px
 *  at a 1080p frame (short side 1080); renderers scale by designScale(). */
export interface TextLayerStyle {
  /** A font id from `myFonts`, or a legacy AVAILABLE_FONTS[].name. Resolved by
   *  `getFontData` (src/lib/get-font-data.ts), which handles both. */
  fontName: string;
  /**
   * Direct URL to the font file. Mirrors `Subtitle.tsx`'s `fontUrl` prop and
   * exists for the same reason: the headless Remotion bundle is built without
   * `VITE_API_URL` (see Remotion.dockerfile), so `getFontData` cannot look a
   * backend font id up at render time. Without this, a custom font renders
   * correctly in the preview and silently falls back to sans-serif on export.
   * Null for built-in fonts, whose ids resolve to absolute URLs offline.
   */
  fontUrl: string | null;
  fontSize: number;
  color: string;
  backgroundColor: string | null;
  /** 0–1, applied to the background box only (not the text). */
  backgroundOpacity: number;
  /** px at TEXT_REFERENCE_WIDTH, like fontSize. */
  borderRadius: number;
  paddingX: number;
  paddingY: number;
  strokeColor: string | null;
  strokeWidth: number;
  shadow: LayerShadow | null;
  align: 'left' | 'center' | 'right';
  bold: boolean;
  italic: boolean;
}

interface MediaLayerBase {
  id: string;
  name: string;
  /** Seconds on the output clip timeline. */
  start: number;
  end: number;
  /** 0–1. */
  opacity: number;
}

export interface ImageLayer extends MediaLayerBase {
  type: 'image';
  /** Uploaded https URL, or a blob: URL while the upload is in flight. */
  url: string;
  rect: LayerRect;
  /** Degrees. */
  rotation: number;
  /** True while getUploadUrl is in flight (url is a blob: URL). */
  uploading: boolean;
}

/** Reframe applied within a rect: uniform zoom (1–3x) plus a fractional pan
 *  offset (−1..1) of the zoomed content. Neutral = { scale: 1, offsetX: 0, offsetY: 0 }. */
export interface LayerFrame {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export interface VideoLayer extends MediaLayerBase {
  type: 'video';
  url: string;
  rect: LayerRect;
  rotation: number;
  uploading: boolean;
  /** 0–1. */
  volume: number;
  /** Intrinsic media duration in seconds ("media dictates length"). */
  mediaDuration: number;
  /** Playback rate, clamped 0.25–4. Window length = (trimEnd − trimStart) / speed. */
  speed: number;
  /** Media in-point, seconds in media time. */
  trimStart: number;
  /** Media out-point, seconds in media time. */
  trimEnd: number;
  /** Where this layer's media comes from. Absent ⇒ 'upload' (legacy rows). */
  source?: 'upload' | 'clip';
  /** Optional zoom/pan reframe applied within `rect`. */
  frame?: LayerFrame;
}

export interface AudioLayer extends MediaLayerBase {
  type: 'audio';
  url: string;
  uploading: boolean;
  volume: number;
  mediaDuration: number;
  /** Playback rate, clamped 0.25–4. Window length = (trimEnd − trimStart) / speed. */
  speed: number;
  /** Media in-point, seconds in media time. */
  trimStart: number;
  /** Media out-point, seconds in media time. */
  trimEnd: number;
  /** Catalog id when this is a sound effect from the sound-effect library. */
  sfxId?: string;
}

export interface TextLayer extends MediaLayerBase {
  type: 'text';
  text: string;
  style: TextLayerStyle;
  rect: LayerRect;
  rotation: number;
}

export interface MotionLayer extends MediaLayerBase {
  type: 'motion';
  scene: MotionScene;
  rect: LayerRect;
  rotation: number;
}

export type MediaLayer = ImageLayer | VideoLayer | AudioLayer | TextLayer | MotionLayer;

/** Layers that occupy a rect on the canvas (everything but audio). */
export type VisualLayer = ImageLayer | VideoLayer | TextLayer | MotionLayer;

export function isVisualLayer(l: MediaLayer): l is VisualLayer {
  return l.type !== 'audio';
}

/** Layers that go through the upload flow (everything but text). */
export function isUploadableLayer(l: MediaLayer): l is ImageLayer | VideoLayer | AudioLayer {
  return l.type === 'image' || l.type === 'video' || l.type === 'audio';
}

/** A video layer whose media is a mirror of the clip itself, not an upload. */
export function isClipMirror(l: MediaLayer): boolean {
  return l.type === 'video' && l.source === 'clip';
}

/** Wire shape appended to overlays.front[] and consumed by the Remotion render. */
export type LayerOverlayItem =
  | {
      type: 'motion';
      scene: MotionScene;
      start: number;
      end: number;
      layer: { rect: LayerRect; rotation: number; opacity: number; zIndex: number };
    }
  | {
      type: 'image';
      url: string;
      start: number;
      end: number;
      layer: { rect: LayerRect; rotation: number; opacity: number; zIndex: number };
    }
  | {
      type: 'video';
      url: string;
      start: number;
      end: number;
      layer: {
        rect: LayerRect;
        rotation: number;
        opacity: number;
        zIndex: number;
        volume: number;
        speed: number;
        trimStart: number;
        frame?: LayerFrame;
      };
    }
  | {
      type: 'clip';
      start: number;
      end: number;
      layer: {
        rect: LayerRect;
        rotation: number;
        opacity: number;
        zIndex: number;
        volume: number;
        frame?: LayerFrame;
      };
    }
  | {
      type: 'audio';
      url: string;
      start: number;
      end: number;
      layer: { opacity: number; zIndex: number; volume: number; speed: number; trimStart: number };
    }
  | {
      type: 'text';
      text: string;
      style: TextLayerStyle;
      start: number;
      end: number;
      layer: { rect: LayerRect; rotation: number; opacity: number; zIndex: number };
    };
